import {useTranslation} from 'react-i18next';
import {ArrowRight} from 'lucide-react';
import {Button, Card} from '@chekinapp/ui';
import type {Guidebook} from '@chekin/core';
import {HighlightedTitle} from '../../components/PageLayoutHeader';
import {GuidebookRow} from '../guidebooks/list/GuidebookRow';
import {SectionHeading} from './SectionHeading';

const MAX_HOME_GUIDEBOOKS = 4;

type Props = {
  guidebooks: Guidebook[];
  openGuidebook: (guidebook: Guidebook, source: string) => void;
  openAllGuidebooks?: (source: string) => void;
  id?: string;
};

function SeeAllLink({count, onClick}: {count: number; onClick: () => void}) {
  const {t} = useTranslation();
  return (
    <Button
      variant="link"
      onClick={onClick}
      className="shrink-0 gap-1 hover:underline hover:opacity-100 [&_svg]:size-[13px]"
    >
      {t('see_all')} ({count})
      <ArrowRight />
    </Button>
  );
}

export function HomeGuidebooksSection({
  guidebooks,
  openGuidebook,
  openAllGuidebooks,
  id,
}: Props) {
  const {t} = useTranslation();

  if (guidebooks.length === 0) return null;

  const isTruncated =
    Boolean(openAllGuidebooks) && guidebooks.length > MAX_HOME_GUIDEBOOKS;
  const visible = isTruncated ? guidebooks.slice(0, MAX_HOME_GUIDEBOOKS) : guidebooks;

  return (
    <section id={id} data-testid="home-guidebooks-section" className="scroll-mt-6">
      <div className="mb-3 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <SectionHeading className="text-[18px] leading-[22px]">
            <HighlightedTitle i18nKey="home_guidebooks_title" />
          </SectionHeading>
          <p className="mt-1 text-[14px] font-medium leading-[21px] text-[var(--chekin-text-secondary)]">
            {t('home_guidebooks_description')}
          </p>
        </div>
        {isTruncated && openAllGuidebooks ? (
          <SeeAllLink
            count={guidebooks.length}
            onClick={() => openAllGuidebooks('guidebooks')}
          />
        ) : null}
      </div>
      <Card className="overflow-hidden px-0 py-0.5">
        {visible.map(guidebook => (
          <GuidebookRow
            key={guidebook.id}
            guidebook={guidebook}
            onClick={() => openGuidebook(guidebook, 'guidebook_row')}
          />
        ))}
      </Card>
    </section>
  );
}
